import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { StoH4Status } from '@contracts/stoH4';
import { Banner, Panel } from '../components/Panel';
import { api } from '../lib/api';

/** Guide of the separate Sto-H4 system: method, pros/cons and the backtest summary. Visible to every approved user. */
export function GuideStoH4Page() {
  const q = useQuery({ queryKey: ['sto-h4-status'], queryFn: () => api.get<StoH4Status>('/api/v1/sto-h4/status') });
  const ch = q.data?.channels;
  return (
    <>
      <header className="page__header">
        <h1>คู่มือ Sto-H4</h1>
        <p className="page__lede">
          ระบบแจ้งเตือน <strong>Stochastic H4 + ยืนยันด้วย H1</strong> แยกจากระบบอื่นทั้งหมด (ตาราง หน้า
          และสวิตช์ของตัวเอง) · ดูสถานะและไม้ที่เปิดอยู่ได้ที่หน้า <Link to="/sto-h4">Sto-H4</Link>
        </p>
      </header>

      <Banner tone="warn" title="ระบบนี้ไม่มี Stop Loss — แจ้งเตือนเท่านั้น ไม่ส่งคำสั่งซื้อขาย">
        ไม้ที่ผิดทางจะถูกถือค้างจนกว่าจะกลับมากำไร ผลทดสอบย้อนหลังไม่รับประกันผลในอนาคต ไม่ใช่คำแนะนำการลงทุน
      </Banner>

      <div className="stack" style={{ marginTop: 16 }}>
        <Panel title="1. วิธีการ">
          <ul className="guide-list">
            <li>
              <strong>เครื่องมือ:</strong> กราฟแท่งเทียน + Stochastic (5,3,3) อย่างเดียว ราคา Bid จาก MT5
            </li>
            <li>
              <strong>ตรวจเมื่อ:</strong> ทุกครั้งที่แท่ง <strong>H4 ปิด</strong> (ช่วงเวลาแท่งเดียวกับกราฟ H4 ใน MT5)
            </li>
            <li>
              <strong>BUY:</strong> Stoch H4 %K <strong>ตัดขึ้น</strong> เหนือ %D จาก<strong>ใต้ 20</strong>{' '}
              (แท่งที่เพิ่งปิดหรือแท่งก่อนหน้า)
              <strong> และ</strong> Stoch H1 %K <strong>ต่ำกว่า 20</strong> อย่างน้อย 1 ใน 4 แท่ง H1 ล่าสุด →
              เข้าที่ราคา Ask ตอนนั้น
            </li>
            <li>
              <strong>SELL:</strong> กลับด้าน — Stoch H4 ตัดลงจาก<strong>เหนือ 80</strong> และ Stoch H1 สูงกว่า 80 →
              เข้าที่ราคา Bid
            </li>
            <li>
              <strong>ไม่มี SL ไม่มี TP:</strong> ทุกสัญญาณเปิดไม้ใหม่ 0.01 lot แยกกัน เปิดซ้อนกันได้หลายไม้
            </li>
            <li>
              <strong>ปิดกำไร:</strong> ตอน H4 ปิด ถ้า Stoch H4 ถึงฝั่งตรงข้าม (BUY: %K ≥ 80 · SELL: %K ≤ 20){' '}
              <strong>และ</strong> ไม้นั้นกำลังกำไร → แจ้งปิดกำไร · ถ้ายังขาดทุน <strong>ถือต่อ</strong>
            </li>
            <li>
              <strong>เริ่มนับไม้:</strong> ตั้งแต่ระบบเริ่มทำงาน (ข้อมูลย้อนหลังใช้คำนวณ Stoch เท่านั้น)
            </li>
            <li>
              <strong>แจ้งเตือน:</strong> หน้าเว็บ ✓ · แอปมือถือ {ch?.push ? '✓' : '(ปิด)'} · LINE{' '}
              {ch?.line ? '✓' : '(ปิด)'} — ถึงผู้ใช้ที่เปิดรับ “สัญญาณ BUY/SELL” ในหน้า Settings
            </li>
          </ul>
        </Panel>

        <Panel title="2. ข้อดี">
          <ul className="guide-list">
            <li>ใช้เครื่องมือเดียว เข้าใจง่าย ตรวจตามได้บนกราฟ MT5</li>
            <li>ไม้ส่วนใหญ่ปิดเร็ว — ค่ากลางถือประมาณ 5 วัน กำไรทั่วไป $40–70 ต่อไม้</li>
            <li>ไม้ที่ปิดแล้วกำไรทุกไม้ (เพราะปิดเฉพาะตอนกำไร)</li>
            <li>ทำงานได้ดีในตลาดที่แกว่งขึ้นลงแล้วกลับมาที่เดิม</li>
          </ul>
        </Panel>

        <Panel title="3. ข้อเสีย / ความเสี่ยง">
          <ul className="guide-list">
            <li>
              <strong>ไม่มี SL:</strong> ไม้ที่ผิดทางค้างได้นานหลายเดือน (ทดสอบพบถือถึง 659 วันและยังไม่ปิด)
            </li>
            <li>
              <strong>ต้องใช้ทุนหนามาก:</strong> ไม้เดียวเคยติดลบลึก −$2,946 · ไม้ค้างรวมกันเคยติดลบ −$48,124
            </li>
            <li>
              <strong>ขาดทุนหนักเมื่อตลาดวิ่งเทรนด์ยาว:</strong> สัญญาณส่วนใหญ่ (73%) เป็น SELL
              ช่วงทองขึ้นแรงจึงค้างสวนเทรนด์
            </li>
            <li>“ชนะทุกไม้” มาจากการไม่ปิดไม้ที่ขาดทุน — ต้องดูกำไรสุทธิรวมไม้ค้างเสมอ</li>
            <li>ถือนานต้องใช้โบรกที่ไม่มีค่า Swap</li>
          </ul>
        </Panel>

        <Panel
          title="4. ผลทดสอบย้อนหลัง (สรุป)"
          meta="ข้อมูล MT5 ถึง 29 ต.ค. 2026 · ต่อไม้ 0.01 lot · หักสเปรดแล้ว ไม่คิด Swap"
        >
          <div className="table-wrap">
            <table className="ledger">
              <caption className="visually-hidden">ผลทดสอบย้อนหลัง Sto-H4</caption>
              <thead>
                <tr>
                  <th>ช่วง</th>
                  <th className="r">สัญญาณ</th>
                  <th className="r">ปิดแล้ว</th>
                  <th className="r">ค้างอยู่</th>
                  <th className="r">สุทธิ (รวมไม้ค้าง)</th>
                  <th className="r">ติดลบรวมลึกสุด</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1 ปี</td>
                  <td className="r">41</td>
                  <td className="r">39 ไม้ +$3,622</td>
                  <td className="r">2 ไม้ −$777</td>
                  <td className="r pos">≈ +$2,845</td>
                  <td className="r">หลายพัน $</td>
                </tr>
                <tr>
                  <td>2 ปี</td>
                  <td className="r">94</td>
                  <td className="r">75 ไม้ +$5,139</td>
                  <td className="r">19 ไม้ −$12,459</td>
                  <td className="r neg">≈ −$7,320</td>
                  <td className="r neg">−$48,124</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ul className="guide-list" style={{ marginTop: 8 }}>
            <li>ไม้ที่ปิดแล้ว: กำไรเฉลี่ย $68.5 (ค่ากลาง $41.8) · ถือเฉลี่ย 16.6 วัน (ค่ากลาง 5.2 วัน)</li>
            <li>BUY 25 ไม้ — ถือนานสุด 35 วัน · SELL 69 ไม้ — ถือนานสุด 211 วัน และค้างอยู่ 17 ไม้</li>
            <li>
              ปีล่าสุดผลดีเพราะทองแกว่งขึ้นลงแล้วกลับมาที่เดิม (~4,000 → 5,600 → ~4,000) · เมื่อรวมปี 2025 ที่ทองขึ้นยาว
              ผลสุทธิกลายเป็นขาดทุน
            </li>
            <li>ไม้เปิดพร้อมกันสูงสุด 28 ไม้ (ถ้าใช้ทุน $2,000 ต่อไม้ ต้องใช้ ~$56,000)</li>
          </ul>
        </Panel>
      </div>
    </>
  );
}
